import React, { memo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import WorkflowDiagram from './WorkflowDiagram';
import WorkflowPrompt from './WorkflowPrompt';
import WorkflowRunRow from './WorkflowRunRow';
import PickerMenu from '../../../shared/components/PickerMenu';
import type {
  WorkflowCatalogEntry,
  WorkflowRunDetail,
  WorkflowRunSummary,
} from '../../../types/backend';

interface Props {
  runs: WorkflowRunSummary[];
  loading: boolean;
  error: string | null;
  /** The run drawn below the list. Nothing selected means nothing is drawn. */
  selectedRunId: string | null;
  onSelectRun: (runId: string) => void;
  detail: WorkflowRunDetail | undefined;
  detailLoading: boolean;
  onExpand: () => void;
  /** The catalogue, so this button opens the same picker the chat control opens. */
  workflows: WorkflowCatalogEntry[];
  /** Omitted while workflows is switched off: nothing new starts. */
  onRunWorkflow?: (workflowName: string) => void;
  /** Both omitted while workflows is switched off: a run under way is read, not moved. */
  onResolveGate?: (nodeId: string, approved: boolean, note?: string) => Promise<void>;
  onTakeBranch?: (nodeId: string, condition: string) => Promise<void>;
  /** Offered on each row, so a blocked run does not have to be opened to be dealt with. Retry is
   *  omitted while workflows is switched off; abandon stays (FS-UC-02 3a). */
  onRetryStep?: (runId: string, nodeId: string, stepLabel: string) => Promise<void>;
  onAbandonRun?: (runId: string, reason: string) => Promise<void>;
}

// Every workflow this case is running, and one of them drawn. The list answers "what is going on
// and does any of it need me"; the drawing below answers "what is this one actually doing".
//
// They live in one column on purpose. Before this the list was here and the drawing was in a modal,
// so seeing where a run stood meant leaving the case and coming back, and a run waiting on an
// approval looked the same as one quietly working.
const WorkflowRunPanel = memo<Props>(({
  runs,
  loading,
  error,
  selectedRunId,
  onSelectRun,
  detail,
  detailLoading,
  onExpand,
  workflows,
  onRunWorkflow,
  onResolveGate,
  onTakeBranch,
  onRetryStep,
  onAbandonRun,
}) => {
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  // Before the empty state, never instead of it: a listing that could not load is an unknown, and
  // "No workflows running" would present it as a measured zero (PRIN-06).
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const selected = runs.find((r) => r.run_id === selectedRunId);
  const running = new Set(runs.map((r) => r.workflow_name));

  return (
    <Box sx={{ height: '100%', overflowY: 'auto' }}>
      <Box sx={{ p: 1.5 }}>
        {runs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <AccountTreeIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No workflows running
            </Typography>
            {onRunWorkflow && (
              <Typography variant="caption" color="text.secondary">
                Run one below, or ask the agent in the chat
              </Typography>
            )}
          </Box>
        ) : (
          runs.map((run) => (
            <WorkflowRunRow
              key={run.run_id}
              run={run}
              selected={run.run_id === selectedRunId}
              onSelect={onSelectRun}
              onRetryStep={onRetryStep}
              onAbandonRun={onAbandonRun}
            />
          ))
        )}

        {onRunWorkflow ? (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => setPickerAnchor(e.currentTarget)}
              sx={{ mt: 0.5 }}
            >
              Run workflow
            </Button>

            <PickerMenu
              anchorEl={pickerAnchor}
              onClose={() => setPickerAnchor(null)}
              caption="Run as many as the case needs."
              items={workflows.map((workflow) => ({
                id: workflow.name,
                title: workflow.title,
                description: `${workflow.node_count} steps · ${workflow.layer_count} stages`,
                action: running.has(workflow.name) ? 'Open' : 'Run',
              }))}
              onPick={(name) => {
                const existing = runs.find((r) => r.workflow_name === name);
                if (existing) onSelectRun(existing.run_id);
                else onRunWorkflow(name);
              }}
              emptyText="No workflows available"
            />
          </>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Workflows are switched off: runs under way can be read and abandoned, nothing new starts.
          </Typography>
        )}
      </Box>

      {selectedRunId && <Divider />}

      {selectedRunId && (
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 1.5, pt: 1.5, pb: 1 }}
          >
            <Typography variant="subtitle2" noWrap sx={{ minWidth: 0 }}>
              {selected?.workflow_title ?? 'Workflow run'}
            </Typography>
            <Button size="small" onClick={onExpand} disabled={!detail}>
              Expand
            </Button>
          </Stack>

          {detailLoading && !detail && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={18} />
            </Box>
          )}

          {/* A run whose pinned version was deleted has no shape to draw and no step to answer.
              The row above already says so, and the full view holds its record. */}
          {detail && detail.plan && (
            <>
              <Box sx={{ px: 1.5, pb: 1.5 }}>
                <WorkflowDiagram nodes={detail.plan.nodes} run={detail.run} dense />
              </Box>
              {onResolveGate && onTakeBranch && (
                <WorkflowPrompt
                  key={detail.run.run_id}
                  run={detail.run}
                  nodes={detail.plan.nodes}
                  onResolveGate={onResolveGate}
                  onTakeBranch={onTakeBranch}
                />
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
});

WorkflowRunPanel.displayName = 'WorkflowRunPanel';

export default WorkflowRunPanel;
